import { RangeCheckProps } from '../types/ProductionValueCheck';

/**
 * @description Checks whether the value is on range or not. Should follow the same comparison logic as BE,
 * just returning additional data.
 */
export function checkValueChangeTolerance({ originalValue, newValue, settings }: RangeCheckProps): {
  valid: boolean;
  percentDifference: number;
  difference: number;
} {
  if (originalValue === newValue) {
    return {
      valid: true,
      percentDifference: 0,
      difference: 0,
    };
  }

  let difference = 0;
  let percentDifference = 0;

  if (originalValue > newValue) {
    difference = originalValue - newValue;
    percentDifference = difference / originalValue;
    if (settings && percentDifference > settings.maxProductionDecrease) {
      return {
        valid: false,
        percentDifference,
        difference,
      };
    }
  } else {
    difference = newValue - originalValue;
    percentDifference = difference / originalValue;
    if (settings && percentDifference > settings.maxProductionIncrease) {
      return {
        valid: false,
        percentDifference,
        difference,
      };
    }
  }

  return {
    valid: true,
    percentDifference,
    difference,
  };
}

interface ValueChangeCheckParams {
  newValue: number;
  currentValue: number;
  contractedValue?: number | null;
  settings?: RangeCheckProps['settings'];
}

export interface ValueChangeCheckResult {
  valid: boolean;
  difference: number;
  percentDifference: number;
  changed: boolean;
  baseValue: number;
}

export function getValueChangeInfo({
  newValue,
  currentValue,
  contractedValue,
  settings,
}: ValueChangeCheckParams): ValueChangeCheckResult {
  const hasContracted = contractedValue != null && contractedValue > 0;
  const baseValue = hasContracted ? contractedValue : currentValue;
  if (newValue === baseValue) {
    return {
      valid: true,
      difference: 0,
      percentDifference: 0,
      changed: false,
      baseValue,
    };
  }

  const rangeResult = checkValueChangeTolerance({
    originalValue: baseValue,
    newValue,
    settings,
  });
  const valid = hasContracted ? rangeResult.valid : true;
  const signedPercent = newValue < baseValue ? rangeResult.percentDifference * -1 : rangeResult.percentDifference;
  const changed = signedPercent !== 0;

  return {
    valid,
    difference: rangeResult.difference,
    percentDifference: signedPercent,
    changed,
    baseValue,
  };
}
